import i18n from '@/utils/i18n';
import React from 'react';
import { AdditionalDetailsErrorMap } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/hooks/use-sales-portal-checkout-additional-inputs';
import { createStyles } from '@/contexts/theme-context';
import { getTranslationValue, sanitizeTranslation } from '@/utils/translations';
import { InputValueMap } from '@/utils/new-order';
import { OrderSummaryInput } from '@domain/configuration/types';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { TextInput } from '@/experiences/sales-portal/components/text-input';
import { Typography } from '@usepepper/ui/atoms/typography';

interface CheckoutPanelAdditionalDetailsProps {
  errorMap: AdditionalDetailsErrorMap;
  inputs: OrderSummaryInput[];
  onChange: (id: string, title: string, value: string) => void;
  onClearFieldError: (id: string) => void;
  onValidateField: (id: string) => void;
  style?: StyleProp<ViewStyle>;
  valueMap: InputValueMap;
}

export const CheckoutPanelAdditionalDetails: React.FC<
  CheckoutPanelAdditionalDetailsProps
> = ({
  errorMap,
  inputs,
  onChange,
  onClearFieldError,
  onValidateField,
  style,
  valueMap,
}) => {
  const [styles, theme] = useStyles();
  const handleChange = (id: string, title: string, value: string) => {
    onChange(id, title, value);
    onClearFieldError(id);
  };

  const renderInputField = (input: OrderSummaryInput) => {
    const title = getTranslationValue(sanitizeTranslation(input.title));
    const placeholder = input.placeholder
      ? getTranslationValue(sanitizeTranslation(input.placeholder))
      : undefined;
    const value = valueMap[input.id]?.value ?? '';
    const isTextArea = input.type === 'textarea';

    return (
      <View key={input.id} style={styles.field}>
        <Typography color={theme.sk.color.text.primary} variant="label-sm">
          {title}
        </Typography>
        <TextInput
          error={errorMap[input.id] ?? null}
          focusedBorderColor={theme.sk.color.border.input.active}
          multiline={isTextArea}
          numberOfLines={isTextArea ? 3 : 1}
          onBlur={() => onValidateField(input.id)}
          onChangeText={(nextValue) => handleChange(input.id, title, nextValue)}
          placeholder={placeholder}
          selectionColor={theme.sk.color.border.input.active}
          style={[
            styles.inputText,
            isTextArea ? styles.inputTextMultiline : null,
          ]}
          testID={`additional-input-${input.id}`}
          textAlignVertical={isTextArea ? 'top' : 'center'}
          value={value}
        />
      </View>
    );
  };

  return (
    <View style={StyleSheet.flatten([styles.container, style])}>
      <Typography color={theme.sk.color.text.primary} variant="heading-xs">
        {i18n.t('sales_portal.checkout_panel.summary.additional_details')}
      </Typography>
      <View style={styles.fields}>{inputs.map(renderInputField)}</View>
    </View>
  );
};

const useStyles = createStyles((theme) => {
  const { lineHeight, ...paragraphMd } = theme.sk.typography.paragraph.md;

  return {
    container: {
      flexDirection: 'column',
      gap: theme.sk.spacing.spaceSmall,
    },
    field: {
      flexDirection: 'column',
      gap: theme.sk.spacing.spaceXsmall,
    },
    fields: {
      flexDirection: 'column',
      gap: theme.sk.spacing.spaceMedium,
    },
    inputText: {
      ...paragraphMd,
      color: theme.sk.color.text.primary,
      fontWeight: 400,
      paddingHorizontal: theme.sk.spacing.spaceSMedium,
    },
    inputTextMultiline: {
      lineHeight,
    },
  };
});
